/**
 * Circular flags for the locale switcher. wehunt.app serves these as SVG files
 * under /images/flags; the web apps do not ship that asset directory, so they
 * are drawn inline here — a flag at 20px is a handful of rectangles and this
 * keeps the switcher working with no new public assets.
 */

export type CountryCode = 'GLOBAL' | 'FI' | 'SE' | 'DE' | 'FR' | 'DK' | 'NO' | 'GB'

/** Nordic cross geometry, in the 0..20 space every flag below is drawn in. */
const CROSS_X = 5.5
const CROSS_W = 4
const CROSS_Y = 8
const CROSS_H = 4

function NordicCross({ field, cross }: { field: string; cross: string }) {
  return (
    <>
      <rect width={20} height={20} fill={field} />
      <rect x={CROSS_X} y={0} width={CROSS_W} height={20} fill={cross} />
      <rect x={0} y={CROSS_Y} width={20} height={CROSS_H} fill={cross} />
    </>
  )
}

function VerticalBands({ colors }: { colors: readonly [string, string, string] }) {
  return (
    <>
      {colors.map((c, i) => (
        <rect key={c + i} x={(20 / 3) * i} y={0} width={20 / 3 + 0.01} height={20} fill={c} />
      ))}
    </>
  )
}

function HorizontalBands({ colors }: { colors: readonly [string, string, string] }) {
  return (
    <>
      {colors.map((c, i) => (
        <rect key={c + i} x={0} y={(20 / 3) * i} width={20} height={20 / 3 + 0.01} fill={c} />
      ))}
    </>
  )
}

function UnionJack() {
  return (
    <>
      <rect width={20} height={20} fill="#012169" />
      <path d="M0 0 L20 20 M20 0 L0 20" stroke="#fff" strokeWidth={4} />
      <path d="M0 0 L20 20 M20 0 L0 20" stroke="#c8102e" strokeWidth={2} />
      <path d="M10 0 V20 M0 10 H20" stroke="#fff" strokeWidth={6} />
      <path d="M10 0 V20 M0 10 H20" stroke="#c8102e" strokeWidth={3.5} />
    </>
  )
}

/** A globe stands in for "Global", matching the site's country-agnostic entry. */
function Globe() {
  return (
    <>
      <rect width={20} height={20} fill="#2f6b8f" />
      <g stroke="#eaf2f6" strokeWidth={1} fill="none">
        <circle cx={10} cy={10} r={7} />
        <path d="M3 10 H17 M10 3 v14 M10 3 C6 6.5 6 13.5 10 17 M10 3 C14 6.5 14 13.5 10 17" />
      </g>
    </>
  )
}

const FIELDS: Record<CountryCode, () => JSX.Element> = {
  GLOBAL: Globe,
  FI: () => <NordicCross field="#ffffff" cross="#002f6c" />,
  SE: () => <NordicCross field="#006aa7" cross="#fecc00" />,
  DK: () => <NordicCross field="#c8102e" cross="#ffffff" />,
  NO: () => (
    <>
      <rect width={20} height={20} fill="#ba0c2f" />
      <rect x={4.75} y={0} width={5.5} height={20} fill="#fff" />
      <rect x={0} y={7.25} width={20} height={5.5} fill="#fff" />
      <rect x={CROSS_X + 0.75} y={0} width={2.5} height={20} fill="#00205b" />
      <rect x={0} y={CROSS_Y + 0.75} width={20} height={2.5} fill="#00205b" />
    </>
  ),
  DE: () => <HorizontalBands colors={['#000000', '#dd0000', '#ffce00']} />,
  FR: () => <VerticalBands colors={['#002395', '#ffffff', '#ed2939']} />,
  GB: UnionJack,
}

export function FlagIcon({ code, size = 20 }: { code: CountryCode; size?: number }) {
  const Field = FIELDS[code] ?? FIELDS.GLOBAL
  const clipId = `wh-flag-clip-${code}`
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      aria-hidden
      focusable="false"
      style={{ display: 'block', borderRadius: '50%', flexShrink: 0 }}
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx={10} cy={10} r={10} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <Field />
      </g>
    </svg>
  )
}
